import React from 'react';
import { Link } from 'react-router-dom';
import { TreeDeciduous, Gem, ShoppingBag, Wrench, Wine, Scissors, Zap, Layers, Factory, Cog, Palette } from 'lucide-react';
import SEO from '../components/SEO';
import useScrollAnimation from '../hooks/useScrollAnimation';
import './Servicios.css';

const Servicios = () => {
  const [introRef, introVisible] = useScrollAnimation();
  const [laserRef, laserVisible] = useScrollAnimation();
  const [printingRef, printingVisible] = useScrollAnimation();
  const [processRef, processVisible] = useScrollAnimation();

  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Service",
          "name": "Grabado Láser",
          "description": "Servicio profesional de grabado láser en diversos materiales",
          "provider": {
            "@type": "LocalBusiness",
            "name": "Artelaser3D"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Service",
          "name": "Impresión 3D",
          "description": "Servicio profesional de impresión 3D FDM y resina",
          "provider": {
            "@type": "LocalBusiness",
            "name": "Artelaser3D"
          }
        }
      }
    ]
  };

  const laserServices = [
    {
      title: 'Madera',
      description: 'Grabado en madera de pino, roble, nogal, haya y contrachapado. Ideal para señalética, decoración y productos artesanales.',
      icon: <TreeDeciduous size={28} />
    },
    {
      title: 'Acrílico / Plexiglas',
      description: 'Precisión cristalina en materiales acrílicos. Perfecto para trofeos, displays y elementos decorativos modernos.',
      icon: <Gem size={28} />
    },
    {
      title: 'Cuero y Piel',
      description: 'Personalización de artículos de cuero genuino y sintético. Ideal para marroquinería, fundas y accesorios.',
      icon: <ShoppingBag size={28} />
    },
    {
      title: 'Metal Anodizado',
      description: 'Grabado de alta definición en aluminio anodizado. Perfecto para placas identificativas y productos técnicos.',
      icon: <Wrench size={28} />
    },
    {
      title: 'Cristal y Vidrio',
      description: 'Efecto esmerilado elegante en vidrio y cristal. Ideal para regalos corporativos y decoración sofisticada.',
      icon: <Wine size={28} />
    },
    {
      title: 'Corte Láser',
      description: 'Corte preciso de materiales para piezas complejas, maquetas, puzzles y elementos decorativos intrincados.',
      icon: <Scissors size={28} />
    }
  ];

  const printingServices = [
    {
      title: 'Prototipado Rápido',
      description: 'Valida tus diseños antes de la producción. Iteraciones rápidas y económicas para optimizar tus productos.',
      icon: <Zap size={28} />
    },
    {
      title: 'Impresión FDM',
      description: 'Tecnología de filamento para piezas resistentes y funcionales. PLA, PETG, ABS y materiales técnicos.',
      icon: <Layers size={28} />
    },
    {
      title: 'Pequeñas Series',
      description: 'Producción de 10 a 500 unidades con calidad consistente. Perfecto para lanzamientos de producto.',
      icon: <Factory size={28} />
    },
    {
      title: 'Piezas Técnicas',
      description: 'Componentes funcionales con tolerancias precisas. Engranajes, soportes, carcasas y piezas de repuesto.',
      icon: <Cog size={28} />
    },
    {
      title: 'Decoración 3D',
      description: 'Lámparas, jarrones, esculturas y elementos decorativos únicos que darán personalidad a cualquier espacio.',
      icon: <Palette size={28} />
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Consulta Inicial',
      description: 'Cuéntanos tu idea. Te asesoramos sobre la mejor tecnología y materiales para tu proyecto.'
    },
    {
      number: '02',
      title: 'Diseño y Presupuesto',
      description: 'Preparamos un presupuesto detallado sin compromiso. Si es necesario, desarrollamos el diseño 3D.'
    },
    {
      number: '03',
      title: 'Producción',
      description: 'Fabricamos tu proyecto con la máxima precisión y cuidado en cada detalle.'
    },
    {
      number: '04',
      title: 'Control de Calidad',
      description: 'Cada pieza pasa por rigurosos controles antes del acabado final.'
    },
    {
      number: '05',
      title: 'Entrega',
      description: 'Embalamos con cuidado y enviamos a cualquier punto de España. También disponible recogida local.'
    }
  ];

  return (
    <>
      <SEO 
        title="Servicios de Grabado Láser e Impresión 3D"
        description="Descubre todos nuestros servicios de grabado láser e impresión 3D en Asturias. Trabajamos con madera, acrílico, metal, resina y más."
        canonicalUrl="https://artelaser3d.es/servicios"
        schema={schema}
      />

      <section className="page-hero page-hero--services">
        <div className="page-hero__container">
          <span className="page-hero__badge">Nuestros Servicios</span>
          <h1 className="page-hero__title">
            Soluciones integrales de
            <span className="text-primary"> fabricación digital</span>
          </h1>
          <p className="page-hero__description">
            Combinamos tecnología de vanguardia con experiencia artesanal para 
            ofrecerte los mejores resultados en grabado láser e impresión 3D.
          </p>
        </div>
      </section>

      <section 
        ref={introRef}
        className={`section section--intro ${introVisible ? 'section--visible' : ''}`}
      >
        <div className="section__container">
          <div className="services-overview">
            <div className="service-overview-card">
              <div className="service-overview-card__icon">
                <img src="/icons/laser_icono.png" alt="Grabado Láser" className="service-overview-card__icon-img" />
              </div>
              <h2>Grabado Láser</h2>
              <p>
                Precisión milimétrica para personalizar casi cualquier material. 
                Desde regalos únicos hasta producción industrial.
              </p>
              <Link to="/grabado-laser-asturias" className="btn btn--primary">
                Saber más
              </Link>
            </div>

            <div className="service-overview-card">
              <div className="service-overview-card__icon">
                <img src="/icons/impresora_3d_icono.png" alt="Impresión 3D" className="service-overview-card__icon-img" />
              </div>
              <h2>Impresión 3D</h2>
              <p>
                Transforma tus ideas en objetos reales. Prototipos, piezas técnicas 
                y objetos decorativos con la mejor tecnología.
              </p>
              <Link to="/impresion-3d-asturias" className="btn btn--primary">
                Saber más
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section 
        ref={laserRef}
        className={`section section--laser ${laserVisible ? 'section--visible' : ''}`}
      >
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Grabado Láser</span>
            <h2>Materiales y Aplicaciones</h2>
            <p className="section__description">
              Trabajamos con la más amplia variedad de materiales para adaptarnos 
              a las necesidades de tu proyecto.
            </p>
          </header>

          <div className="services-detail-grid">
            {laserServices.map((service, index) => (
              <article key={index} className="service-detail-card">
                <div className="service-detail-card__header">
                  <div className="service-detail-card__icon">{service.icon}</div>
                  <h3>{service.title}</h3>
                </div>
                <p>{service.description}</p>
              </article>
            ))}
          </div>

          <div className="section__cta">
            <Link to="/presupuesto" className="btn btn--primary btn--lg">
              Solicitar presupuesto de grabado láser
            </Link>
          </div>
        </div>
      </section>

      <section 
        ref={printingRef}
        className={`section section--printing ${printingVisible ? 'section--visible' : ''}`}
      >
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Impresión 3D</span>
            <h2>Nuestras Especialidades</h2>
            <p className="section__description">
              Desde prototipos funcionales hasta producción en serie, cubrimos 
              todas tus necesidades de fabricación aditiva.
            </p>
          </header>

          <div className="services-detail-grid">
            {printingServices.map((service, index) => (
              <article key={index} className="service-detail-card service-detail-card--alt">
                <div className="service-detail-card__header">
                  <div className="service-detail-card__icon">{service.icon}</div>
                  <h3>{service.title}</h3>
                </div>
                <p>{service.description}</p>
              </article>
            ))}
          </div>

          <div className="section__cta">
            <Link to="/presupuesto" className="btn btn--primary btn--lg">
              Solicitar presupuesto de impresión 3D
            </Link>
          </div>
        </div>
      </section>

      <section 
        ref={processRef}
        className={`section section--process ${processVisible ? 'section--visible' : ''}`}
      >
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Nuestro Proceso</span>
            <h2>Así trabajamos</h2>
            <p className="section__description">
              Un flujo de trabajo optimizado para garantizar la máxima calidad 
              en cada proyecto.
            </p>
          </header>

          <div className="process-timeline">
            {processSteps.map((step, index) => (
              <article key={index} className="process-step">
                <span className="process-step__number">{step.number}</span>
                <div className="process-step__content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="servicios-cta-wrapper">
        <div className="servicios-cta-container">
          <h2 className="servicios-cta-title">¿Tienes dudas sobre nuestros servicios?</h2>
          <p className="servicios-cta-text">
            Consulta nuestras preguntas frecuentes o contáctanos directamente. 
            Estaremos encantados de resolver todas tus dudas.
          </p>
          <div className="servicios-cta-buttons">
            <Link to="/preguntas-frecuentes" className="servicios-cta-btn servicios-cta-btn--outline">
              Ver FAQ
            </Link>
            <Link to="/contacto" className="servicios-cta-btn servicios-cta-btn--primary">
              Contactar
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Servicios;
